import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import {
  FONT_OPTIONS,
  WEIGHT_OPTIONS,
} from 'dashboard/components/siteRenderer/blockStyles';

/**
 * How a block looks, as opposed to what it says.
 *
 * Every control here writes into `styles`, which the renderer turns into CSS
 * — so the list of fonts and weights comes from the renderer's own module
 * rather than being retyped. A dropdown offering a font the page cannot draw
 * fails silently, and silence is the worst way for this to fail.
 *
 * Unset is a real value throughout: a block with no size keeps the size its
 * section gives it, which is how a hero headline stays a hero headline.
 */
export default function BlockStyleControls({ styles = {}, onChange, showText = true }) {
  const set = (key, value) => onChange({ ...styles, [key]: value });

  const toggles = [
    ...(styles.italic ? ['italic'] : []),
    ...(styles.underline ? ['underline'] : []),
    ...(styles.uppercase ? ['uppercase'] : []),
  ];

  return (
    <Stack spacing='12px'>
      {showText && (
        <>
          <FormControl size='small' fullWidth>
            <InputLabel>Font</InputLabel>
            <Select
              label='Font'
              value={styles.fontFamily || 'default'}
              onChange={(e) => set('fontFamily', e.target.value)}
            >
              {FONT_OPTIONS.map((f) => (
                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction='row' spacing='8px'>
            <FormControl size='small' sx={{ flex: 1 }}>
              <InputLabel>Weight</InputLabel>
              <Select
                label='Weight'
                value={styles.fontWeight || ''}
                onChange={(e) => set('fontWeight', e.target.value)}
              >
                {WEIGHT_OPTIONS.map((w) => (
                  <MenuItem key={w.value || 'default'} value={w.value}>{w.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flex: 1 }}>
              <Typography variant='caption' color='text.secondary' display='block'>
                Size — {styles.fontSize ? `${styles.fontSize}px` : 'automatic'}
              </Typography>
              <Slider
                size='small'
                min={0}
                max={96}
                step={1}
                value={Number(styles.fontSize) || 0}
                // Zero means "leave it alone" rather than a 0px font: the
                // slider needs a bottom end and "automatic" is the one people
                // want to get back to.
                onChange={(_e, v) => set('fontSize', v === 0 ? '' : v)}
                valueLabelDisplay='auto'
              />
            </Box>
          </Stack>

          <Stack direction='row' spacing='8px' flexWrap='wrap' useFlexGap>
            <ToggleButtonGroup
              size='small'
              value={styles.textAlign || ''}
              exclusive
              onChange={(_e, v) => set('textAlign', v || '')}
            >
              <ToggleButton value='left' aria-label='Align left'>
                <FormatAlignLeftIcon fontSize='small' />
              </ToggleButton>
              <ToggleButton value='center' aria-label='Align centre'>
                <FormatAlignCenterIcon fontSize='small' />
              </ToggleButton>
              <ToggleButton value='right' aria-label='Align right'>
                <FormatAlignRightIcon fontSize='small' />
              </ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
              size='small'
              value={toggles}
              onChange={(_e, vals) => onChange({
                ...styles,
                italic: vals.includes('italic'),
                underline: vals.includes('underline'),
                uppercase: vals.includes('uppercase'),
              })}
            >
              <ToggleButton value='italic' aria-label='Italic'>
                <FormatItalicIcon fontSize='small' />
              </ToggleButton>
              <ToggleButton value='underline' aria-label='Underline'>
                <FormatUnderlinedIcon fontSize='small' />
              </ToggleButton>
              <ToggleButton value='uppercase' aria-label='Capitals'>
                <TextFieldsIcon fontSize='small' />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Stack direction='row' spacing='12px'>
            <Box sx={{ flex: 1 }}>
              <Typography variant='caption' color='text.secondary' display='block'>
                Line height — {styles.lineHeight || 'automatic'}
              </Typography>
              <Slider
                size='small'
                min={0}
                max={2.4}
                step={0.1}
                value={Number(styles.lineHeight) || 0}
                onChange={(_e, v) => set('lineHeight', v === 0 ? '' : v)}
                valueLabelDisplay='auto'
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant='caption' color='text.secondary' display='block'>
                Letter spacing — {styles.letterSpacing ? `${styles.letterSpacing}px` : 'normal'}
              </Typography>
              <Slider
                size='small'
                min={-2}
                max={8}
                step={0.5}
                value={Number(styles.letterSpacing) || 0}
                onChange={(_e, v) => set('letterSpacing', v === 0 ? '' : v)}
                valueLabelDisplay='auto'
              />
            </Box>
          </Stack>
        </>
      )}

      <Stack direction='row' spacing='12px'>
        <SwatchField
          label='Text colour'
          value={styles.textColor || ''}
          onChange={(v) => set('textColor', v)}
        />
        <SwatchField
          label='Background'
          value={styles.backgroundColor || ''}
          onChange={(v) => set('backgroundColor', v)}
        />
      </Stack>
    </Stack>
  );
}

/**
 * A colour with a way back to "unset".
 *
 * A native colour input has no empty state — it always reports something — so
 * picking a colour by accident used to be permanent. Clear puts the block
 * back to inheriting its section's colours.
 */
function SwatchField({ label, value, onChange }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography variant='caption' color='text.secondary' noWrap>{label}</Typography>
        {value && (
          <Box
            component='button'
            type='button'
            onClick={() => onChange('')}
            sx={{
              border: 'none', background: 'none', p: 0, cursor: 'pointer',
              font: 'inherit', fontSize: 11, color: 'primary.main',
            }}
          >
            Clear
          </Box>
        )}
      </Stack>
      <Box
        component='input'
        type='color'
        value={value || '#ffffff'}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          width: '100%', height: 32, p: '2px', mt: '2px',
          border: '1px solid', borderColor: 'divider', borderRadius: '8px',
          background: 'transparent', cursor: 'pointer',
          opacity: value ? 1 : 0.45,
        }}
      />
    </Box>
  );
}
